import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import axios from "axios";
import { verifyAndDecodeToken } from "@/utils/jwt";
import axiosInstance, { updateAxiosToken } from "@/utils/axiosInstance";
import { reToken } from "@/libs/auth";

interface IUser {
  id: string;
  email: string;
  username: string;
  status: boolean;
  role: number;
}

interface AuthState {
  userData: IUser | null;
  userMetadata: any;
  token: string;
  logout: () => Promise<void>;
  login: (token: any) => Promise<void>;
  reToken: () => Promise<void>;
  checkLoginStatus: () => Promise<boolean>;
  getTkn: () => Promise<string | null>;
  fetchTokenAndUserData: () => Promise<void>;
  clearStore: () => void;
  // dedupe de fetch concurrente y flags efímeros
  isFetchingAuth: boolean;
  authInitPromise: Promise<void> | null;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      userData: null,
      userMetadata: null,
      token: "",
      isFetchingAuth: false,
      authInitPromise: null,

      clearStore: () => {
        set({ userData: null, userMetadata: null, token: "" });
      },

      logout: async () => {
        get().clearStore();
        try {
          await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL_BASE}/auth/logout`,
            {},
            { withCredentials: true }
          );
        } catch (error) {
          //console.error("Error during logout:", error);
          throw new Error("Error during logout");
        }
      },

      login: async (token: string) => {
        const { error, data } = await verifyAndDecodeToken(token);
        if (error) {
          get().clearStore();
          throw new Error("Error verifying token");
        }
        set({ token });
        updateAxiosToken(token);

        try {
          const userMetadataRes = await axiosInstance.get(
            `${process.env.NEXT_PUBLIC_API_URL_BASE}/users/userdata/${data?.id}`,
            { withCredentials: true }
          );
          const userDataRes = await axiosInstance.get(
            `${process.env.NEXT_PUBLIC_API_URL_BASE}/users/${data?.id}`,
            { withCredentials: true }
          );

          set({
            userMetadata: userMetadataRes.data.userMeta,
            userData: userDataRes.data.user,
          });
        } catch (err: any) {
          console.warn("Error fetching user data:", err);
          get().clearStore();
          throw new Error("Error fetching user data");
        }
      },

      reToken: async () => {
        //console.log("reToken");
        const token = await get().getTkn();
        if (!token) {
          get().clearStore();
          throw new Error("No token available");
        }
        try {
          const res = await reToken(token);
          await get().login(res.token);
        } catch (err: any) {
          //console.error("Error during reToken:", err);
          get().clearStore();
          throw new Error("Error during reToken");
        }
      },

      getTkn: async () => {
        try {
          //console.log("getTkn");
          const tokenRes = await axios.get(
            `${process.env.NEXT_PUBLIC_API_URL_BASE}/auth/token`,
            { withCredentials: true }
          );
          //console.log("tokenRes", tokenRes);
          return tokenRes.data.token;
        } catch (err: any) {
          //console.error("Error fetching token:", err);
          get().clearStore();
          return null;
        }
      },

      checkLoginStatus: async () => {
        //console.log("checkLoginStatus");
        try {

          const tkn = await get().getTkn();
          if (!tkn) {
            get().clearStore();
            return false;
          }
          const { error } = await verifyAndDecodeToken(tkn);
          if (error) {
            get().clearStore();
            return false;
          }
          return true;
        } catch (err) {
          console.warn("Error checking login status:", err);
          get().clearStore();
          return false;
        }
      },

      fetchTokenAndUserData: async () => {
        // dedupe: si ya hay una inicialización en curso, esperar esa promesa
        const existing = get().authInitPromise;
        if (existing) {
          await existing;
          return;
        }

        const initPromise = (async () => {
          try {
            set({ isFetchingAuth: true });

            const tkn = await get().getTkn();

            const { error } = await reToken(tkn);
            if (error) {
              console.warn("Error verifying token:", error);
              get().clearStore();
              return;
            }

            // siempre refrescar: reToken -> login (obtiene datos frescos)
            await get().reToken();
          } catch (err: any) {
            console.warn("Error fetching token/user:", err);
            get().clearStore();
          } finally {
            set({ isFetchingAuth: false, authInitPromise: null });
          }
        })();

        set({ authInitPromise: initPromise });
        await initPromise;
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // Persistir sólo los datos necesarios; no flags/promesas internas
      partialize: (state) => ({
        userData: state.userData,
        userMetadata: state.userMetadata,
        token: state.token,
      }),
    }
  )
);

export default useAuthStore;